import * as React from "react";
import TreatmentCard from "./TreatmentCard";
import IssuesToTreat from "./IssuesToTreat";
import Note, { DeleteNote, UpdateNote } from "./Note";
import AnalysedCriterion from "../commons/model/treatment/AnalysedCriterion";
import CategoryPanel from "../commons/components/CategoryPanel";
import Report, { getTreatmentCardsByCategory } from "../commons/model/report/Report";
import TreatmentSelection from "../commons/model/treatment/TreatmentSelection";

interface AssessmentResultTreatmentOptionsProps {
  report: Report;
  updateNote?: UpdateNote;
  deleteNote?: DeleteNote;
  onTreatmentOptionChange?: (selection: TreatmentSelection) => any;
  onTreatedIssueChange?: (criterion: AnalysedCriterion) => any;
  onNoteChange?: (note: string) => void;
  printMode?: boolean;
  demoMode?: boolean;
  selectedTreatmentSelection?: TreatmentSelection;
}

interface AssessmentResultTreatmentOptionsState {
  focusedSelectionUid?: string;
}

export default class AssessmentResultTreatmentOptions extends React.Component<
  AssessmentResultTreatmentOptionsProps,
  AssessmentResultTreatmentOptionsState
> {
  constructor(props: AssessmentResultTreatmentOptionsProps) {
    super(props);
    this.state = {
      focusedSelectionUid: null,
    };
    this.checkFocus = this.checkFocus.bind(this);
  }

  render() {
    return (
      <>
        {this.props.printMode && (
          <div className="printable-title">
            {i18n["Page.AssessmentResult.Tab.TreatmentOptions"]}
          </div>
        )}
        {(this.props.updateNote || this.props.demoMode) && (
          <IssuesToTreat
            issues={this.props.report.treatmentSelections.reduce(
              (acc, it) => (acc.push(...it.criteria), acc),
              []
            )}
            onTreatedIssueChange={this.props.onTreatedIssueChange}
          />
        )}
        {getTreatmentCardsByCategory(this.props.report).map(({ id, label, selections }) => (
          <CategoryPanel key={id} id={id} label={label}>
            {selections.map((selection: TreatmentSelection) =>
              selection.selectedTreatments.length > 0 ||
              this.state.focusedSelectionUid === selection.uid ? (
                <TreatmentCard
                  key={selection.uid}
                  selection={selection}
                  onTreatmentOptionChange={this.props.onTreatmentOptionChange}
                  printMode={this.props.printMode}
                  demoMode={this.props.demoMode}
                  categoryId={id}
                />
              ) : selection.apiUrl || this.props.demoMode ? (
                <div
                  key={selection.uid}
                  id={"selection_" + selection.uid}
                  className="message warning"
                >
                  <div>
                    <span>
                      {i18n[
                        "Page.AssessmentResult.Treatment.Warning.NoTreatmentSelected.Message"
                      ].replace(
                        "{}",
                        selection.criteria.map((criterion) => criterion.criterionLabel).join(", ")
                      )}
                    </span>
                    &nbsp;
                    <span className="link" onClick={() => this.setSelectionToDefault(selection)}>
                      {i18n["Page.AssessmentResult.Treatment.Warning.NoTreatmentSelected.Action"]}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="message warning" id={"selection_" + selection.uid}>
                  <span>
                    {i18n[
                      "Page.AssessmentResult.Treatment.Warning.NoTreatmentSelected.Readonly.Message"
                    ].replace(
                      "{}",
                      selection.criteria.map((criterion) => criterion.criterionLabel).join(", ")
                    )}
                  </span>
                </div>
              )
            )}
          </CategoryPanel>
        ))}
        <Note
          content={this.props.report.note}
          onNoteChange={this.props.onNoteChange}
          updateNote={this.props.updateNote}
          deleteNote={this.props.deleteNote}
          maxLength={this.props.report.noteMaxLength}
          noteTextAreaRows={this.props.report.noteTextAreaRows}
        />
      </>
    );
  }

  componentDidMount() {
    this.checkFocus = this.checkFocus.bind(this);
    document.addEventListener("click", this.checkFocus);
    window.setTimeout(() => {
      if (this.props.selectedTreatmentSelection) {
        document
          .querySelector("#selection_" + this.props.selectedTreatmentSelection.uid)
          .scrollIntoView({
            block: "start",
            behavior: "smooth",
          });
      }
    }, 0);
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.checkFocus);
  }

  private checkFocus(event) {
    let elt = event.target;
    let selectionUid = null;
    do {
      if (elt.classList.contains("treatment-option-selector")) {
        selectionUid = elt.id;
        break;
      }
      elt = elt.parentElement;
    } while (elt && elt.classList);
    if (selectionUid !== this.state.focusedSelectionUid) {
      this.setState({ focusedSelectionUid: selectionUid });
    }
  }

  private setSelectionToDefault(selection: TreatmentSelection) {
    const newSelection = { ...selection };
    newSelection.selectedTreatments = [...selection.autoAssignedTreatments];
    this.props.onTreatmentOptionChange(newSelection);
  }
}
